"use client"

import { useAtomValue } from "jotai"
import React from "react"

import useIsPageChapterFrontPage from "@/hooks/course-material/useIsPageChapterFrontPage"
import BreakFromCentered from "@/shared-module/common/components/Centering/BreakFromCentered"
import { omitUndefined } from "@/shared-module/common/utils/nullability"
import withErrorBoundary from "@/shared-module/common/utils/withErrorBoundary"
import { currentPageDataAtom } from "@/state/course-material/selectors"

import type { BlockRendererProps } from ".."
import type { HeroSectionProps } from "../../HeroSection"
import HeroSection from "../../HeroSection"

const HERO_SECTION_LABEL_PLACEHOLDER = "Chapter number..."

/** "chapter 2" for any page under /chapter-2, or undefined outside chapters. */
function chapterLabelFromPath(urlPath: string | undefined): string | undefined {
  const chapterNumber = urlPath?.match(/^\/chapter-(\d+)(?:\/|$)/)?.[1]
  return chapterNumber === undefined ? undefined : `chapter ${chapterNumber}`
}

const HeroSectionBlock: React.FC<React.PropsWithChildren<BlockRendererProps<HeroSectionProps>>> = (
  props,
) => {
  const DEFAULT = true
  const pageData = useAtomValue(currentPageDataAtom)
  const path = pageData?.url_path
  const formattedPath = path?.replace("-", " ").replace("/", "")
  const useDefaultTextForLabel = props.data.attributes.useDefaultTextForLabel ?? DEFAULT
  const isChapterFrontpage = useIsPageChapterFrontPage(pageData?.id).data?.is_chapter_front_page
  const partiallyTransparent = props.data.attributes.partiallyTransparent ?? DEFAULT

  const storedLabel = props.data.attributes.label
  const isLabelUnset = !storedLabel || storedLabel === HERO_SECTION_LABEL_PLACEHOLDER
  const automaticLabel = isChapterFrontpage
    ? formattedPath
    : isLabelUnset
      ? (chapterLabelFromPath(path) ?? "")
      : undefined
  const chapterNumber =
    useDefaultTextForLabel && automaticLabel !== undefined ? automaticLabel : storedLabel

  return (
    <BreakFromCentered sidebar={false}>
      <HeroSection
        {...omitUndefined({ label: chapterNumber })}
        title={props.data.attributes.title}
        subtitle={props.data.attributes.subtitle}
        {...omitUndefined({ backgroundImage: props.data.attributes.backgroundImage })}
        partiallyTransparent={partiallyTransparent}
        {...omitUndefined({ fontColor: props.data.attributes.fontColor })}
        alignCenter={props.data.attributes.alignCenter ?? DEFAULT}
        {...omitUndefined({ backgroundColor: props.data.attributes.backgroundColor })}
        {...omitUndefined({ backgroundRepeatX: props.data.attributes.backgroundRepeatX })}
        alignBottom={props.data.attributes.alignBottom}
      />
    </BreakFromCentered>
  )
}

export default withErrorBoundary(HeroSectionBlock)
